import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import BazarCard from '../components/Bazar/BazarCard';
import BazarFilterBar from '../components/Bazar/BazarFilterBar';
import { fetchBazars } from '../utils/bazarApi';
import styles from './Bazar.module.css';

export default function Bazar() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, userData } = useAuth();

  const [bazars, setBazars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingCount, setLoadingCount] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    categoria: null,
    condizione: null,
    stato: ['disponibile', 'prenotato'], // Default
    prezzoMin: null,
    prezzoMax: null,
    negoziabile: null,
    soloMiei: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 15,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
    pageCount: 0,
  });

  const cardBg = useColorModeValue('white', 'gray.700');

  // Carica il conteggio totale degli oggetti nel bazar
  const loadTotalCount = async () => {
    try {
      setLoadingCount(true);
      const data = await fetchBazars(
        { stato: ['disponibile', 'prenotato'] },
        1,
        1
      );
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      console.error('Errore nel conteggio oggetti:', error);
    } finally {
      setLoadingCount(false);
    }
  };

  const loadBazars = useCallback(
    async (page = 1, append = false) => {
      try {
        setLoading(true);
        const { pageSize } = pagination;

        // Se il filtro "soloMiei" è attivo, carica tutti gli oggetti e pagina lato client
        if (filters.soloMiei && isAuthenticated && userData?.email) {
          // Carica tutti gli oggetti (usa un pageSize grande per ottenere tutto)
          const allData = await fetchBazars(filters, 1, 1000);
          const allBazars = allData.bazars || [];

          // Filtra per email dell'utente
          const filteredBazars = allBazars.filter((bazar) => {
            const bazarData = bazar.attributes || bazar;
            const socio = bazarData.socio?.data || bazarData.socio;
            const socioEmail = socio?.email || socio?.attributes?.email;
            return socioEmail === userData.email;
          });

          // Calcola la paginazione lato client
          const total = filteredBazars.length;
          const pageCount = Math.ceil(total / pageSize);
          const startIndex = (page - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedBazars = filteredBazars.slice(startIndex, endIndex);

          setPagination({
            page,
            pageSize,
            total,
            pageCount,
            hasNextPage: page < pageCount,
            hasPrevPage: page > 1,
          });

          setBazars(paginatedBazars);
        } else {
          // Paginazione normale dal backend
          const data = await fetchBazars(filters, page, pageSize);
          const bazarsList = data.bazars || [];
          const paginationData = data.pagination || {};

          setPagination({
            page,
            pageSize,
            total: paginationData.total || 0,
            pageCount:
              paginationData.pageCount ||
              Math.ceil((paginationData.total || 0) / pageSize),
            hasNextPage:
              page <
              (paginationData.pageCount ||
                Math.ceil((paginationData.total || 0) / pageSize)),
            hasPrevPage: page > 1,
          });

          // Se append è true, aggiungi alla lista esistente, altrimenti sostituisci
          if (append) {
            setBazars((prev) => [...prev, ...bazarsList]);
          } else {
            setBazars(bazarsList);
          }
        }
      } catch (error) {
        toast({
          title: 'Errore nel caricamento',
          description: 'Impossibile caricare gli oggetti del bazar',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoading(false);
      }
    },
    [filters, isAuthenticated, userData, pagination.pageSize]
  );

  // Carica il conteggio totale all'avvio
  useEffect(() => {
    loadTotalCount();
  }, []);

  // Carica oggetti quando cambiano i filtri, userData, isAuthenticated o currentPage
  useEffect(() => {
    if (isAuthenticated) {
      loadBazars(currentPage, false);
    }
  }, [loadBazars, currentPage, isAuthenticated]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const scrollToContent = useCallback(() => {
    document
      .getElementById('bazar-content')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const heroSection = (
    <section className={styles.hero} aria-label="Hero Bazar">
      <div
        className={styles.heroBg}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/images/bazar-hero.png)`,
        }}
      />
      <div className={styles.heroOverlay} />
      <div className={styles.heroInner}>
        <h1 className={styles.heroTitle}>Bazar CIL</h1>
        <p className={styles.heroDescription}>
          Uno spazio di scambio e riuso tra soci: gli oggetti circolano invece
          di essere accumulati o buttati. Non è un negozio ma
          un&apos;alternativa al consumo inutile—collaborazione, comunità e meno
          spreco.
        </p>
        <div className={styles.heroButtons}>
          {isAuthenticated ? (
            <Link to="/bazar/nuovo" className={styles.heroBtnPrimary}>
              Inserisci un oggetto
            </Link>
          ) : (
            <Link to="/tesseramento" className={styles.heroBtnPrimary}>
              Accedi al Bazar
            </Link>
          )}
          <button
            type="button"
            className={styles.heroBtnSecondary}
            onClick={scrollToContent}
          >
            {isAuthenticated ? 'Esplora il bazar' : 'Scopri di più'}
          </button>
        </div>
      </div>
    </section>
  );

  // Se l'utente non è autenticato, mostra il messaggio con CTA
  if (!isAuthenticated) {
    return (
      <Box>
        {heroSection}
        <Container id="bazar-content" maxW="container.xl" py={8}>
          <VStack spacing={8} align="stretch">
            {/* Messaggio per non autenticati */}
            <Box
              bg={useColorModeValue('white', 'gray.800')}
              borderRadius="lg"
              p={8}
              border="2px solid"
              borderColor={useColorModeValue('brand.100', 'brand.200')}
              textAlign="center"
            >
              <VStack spacing={6}>
                <VStack spacing={3}>
                  <Heading
                    size="lg"
                    color={useColorModeValue('gray.800', 'white')}
                  >
                    Accedi al Bazar CIL
                  </Heading>
                  <Text
                    fontSize="md"
                    color={useColorModeValue('gray.600', 'gray.300')}
                  >
                    Il Bazar è riservato ai soci CIL. Per accedere e iniziare a
                    scambiare oggetti, devi essere tesserato.
                  </Text>
                  {!loadingCount && totalCount > 0 && (
                    <Text fontSize="lg" fontWeight="bold" color="brand.100">
                      Attualmente ci sono {totalCount} oggetti disponibili nel
                      Bazar
                    </Text>
                  )}
                </VStack>
                <Button
                  size="lg"
                  colorScheme="brand"
                  onClick={() => navigate('/tesseramento')}
                  px={8}
                  py={6}
                  fontSize="lg"
                  fontWeight="bold"
                >
                  Diventa Socio CIL
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      {heroSection}
      <Container id="bazar-content" maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Barra filtri */}
          <BazarFilterBar
            filters={filters}
            onFiltersChange={handleFiltersChange}
            isAuthenticated={isAuthenticated}
          />

          {/* Lista oggetti */}
          {loading ? (
            <Center py={12}>
              <Spinner size="xl" color="brand.100" />
            </Center>
          ) : bazars.length === 0 ? (
            <Box bg={cardBg} borderRadius="lg" p={12} textAlign="center">
              <Alert status="info" variant="subtle" borderRadius="md">
                <AlertIcon />
                <VStack spacing={2} align="stretch">
                  <Text fontWeight="medium">
                    {filters.search ||
                    filters.categoria ||
                    filters.condizione ||
                    filters.prezzoMin ||
                    filters.prezzoMax ||
                    filters.negoziabile
                      ? 'Nessun oggetto trovato con questi filtri.'
                      : 'Il Bazar è ancora vuoto. Sii il primo a inserire un oggetto.'}
                  </Text>
                  {isAuthenticated &&
                    !filters.search &&
                    !filters.categoria &&
                    !filters.condizione &&
                    !filters.prezzoMin &&
                    !filters.prezzoMax &&
                    !filters.negoziabile && (
                      <Button
                        leftIcon={<FaPlus />}
                        colorScheme="brand"
                        size="sm"
                        onClick={() => navigate('/bazar/nuovo')}
                        mt={2}
                      >
                        Inserisci un oggetto
                      </Button>
                    )}
                </VStack>
              </Alert>
            </Box>
          ) : (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {bazars.map((bazar, index) => (
                  <BazarCard
                    key={bazar.documentId || bazar.id}
                    bazar={bazar}
                    index={index}
                  />
                ))}
              </SimpleGrid>

              {/* Paginazione */}
              {(pagination.pageCount > 1 ||
                pagination.total > pagination.pageSize ||
                bazars.length === pagination.pageSize) && (
                <Box textAlign="center" pt={6}>
                  <HStack
                    spacing={4}
                    justify="center"
                    align="center"
                    flexWrap="wrap"
                  >
                    <Button
                      variant="outline"
                      colorScheme="brand"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      isDisabled={
                        !pagination.hasPrevPage || loading || currentPage === 1
                      }
                      isLoading={loading && currentPage > 1}
                      size="md"
                    >
                      ← Precedente
                    </Button>

                    <VStack spacing={1}>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={useColorModeValue('gray.700', 'gray.300')}
                      >
                        Pagina {currentPage} di {pagination.pageCount || 1}
                      </Text>
                      {pagination.total > 0 && (
                        <Text
                          fontSize="xs"
                          color={useColorModeValue('gray.500', 'gray.400')}
                        >
                          {pagination.total} oggetti totali
                        </Text>
                      )}
                    </VStack>

                    <Button
                      variant="outline"
                      colorScheme="brand"
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      isDisabled={
                        !pagination.hasNextPage ||
                        loading ||
                        currentPage >= (pagination.pageCount || 1)
                      }
                      isLoading={loading && currentPage < pagination.pageCount}
                      size="md"
                    >
                      Successiva →
                    </Button>
                  </HStack>
                </Box>
              )}
            </>
          )}

          {/* Footer informativo */}
          <Box
            bg={cardBg}
            borderRadius="lg"
            p={6}
            border="1px solid"
            borderColor={useColorModeValue('gray.200', 'gray.600')}
          >
            <Text
              fontSize="sm"
              color={useColorModeValue('gray.600', 'gray.400')}
              textAlign="center"
            >
              Il Bazar è uno spazio tra soci CIL per favorire il riuso e la
              condivisione. CIL non è responsabile degli scambi.
            </Text>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
